import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { consultationService } from '../services/consultationService'
import { userService } from '../services/userService'
import { patientHistoryService } from '../services/patientHistoryService'
import { authService } from '../services/authService'
import {
  Consultation,
  ConsultationFormData,
  ConsultationStatus,
  ConsultationCode,
  ConsultationTerminologySystem,
} from '../types/consultation'
import { terminologyService, TerminologySearchResult } from '../services/terminologyService'
import { maternityService } from '../services/maternityService'
import { Pregnancy } from '../types/maternity'
import { Modal } from './Modal'
import { MaternityVisitForm } from './MaternityVisitForm'
import './ConsultationForm.css'

interface ConsultationFormProps {
  patientId: string
  appointmentId?: string // Optional appointment ID if consultation is started from an appointment
  consultation?: Consultation
  onSuccess?: () => void
  onCancel?: () => void
}

export function ConsultationForm({ patientId, appointmentId, consultation, onSuccess }: ConsultationFormProps) {
  const queryClient = useQueryClient()
  const isEditing = !!consultation

  const [formData, setFormData] = useState<ConsultationFormData>({
    patientId,
    appointmentId: appointmentId || consultation?.appointmentId,
    staffId: consultation?.staffId || '',
    consultationDate: consultation?.consultationDate || new Date().toISOString().split('T')[0],
    startTime: consultation?.startTime || new Date().toTimeString().slice(0, 5),
    endTime: consultation?.endTime || '',
    status: consultation?.status || 'in-progress',
    reason: consultation?.reason || '',
    notes: consultation?.notes || '',
    diagnosis: consultation?.diagnosis || [],
    codes: consultation?.codes || [],
    plan: consultation?.plan || '',
    followUpDate: consultation?.followUpDate || '',
    followUpNotes: consultation?.followUpNotes || '',
    patientInstructions: consultation?.patientInstructions || '',
    instructionsGiven: consultation?.instructionsGiven || false,
    instructionsGivenAt: consultation?.instructionsGivenAt || '',
    instructionsGivenBy: consultation?.instructionsGivenBy || '',
    instructionDocumentIds: consultation?.instructionDocumentIds || [],
  })

  const [newDiagnosis, setNewDiagnosis] = useState('')
  const [selectedSystem, setSelectedSystem] = useState<ConsultationTerminologySystem>('ICPC2')
  const [codeSearch, setCodeSearch] = useState('')
  const [codeResults, setCodeResults] = useState<TerminologySearchResult[]>([])
  const [isSearchingCodes, setIsSearchingCodes] = useState(false)
  const [showMaternityVisitForm, setShowMaternityVisitForm] = useState(false)
  const [selectedPregnancy, setSelectedPregnancy] = useState<Pregnancy | null>(null)

  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  // Fetch existing conditions to check which codes are already added
  const { data: clinicalHistory } = useQuery({
    queryKey: ['patient-clinical-history', patientId],
    queryFn: () => patientHistoryService.getClinicalHistory(patientId),
    enabled: !!patientId,
  })

  // Fetch active pregnancies for maternity visit option
  const { data: pregnancies } = useQuery({
    queryKey: ['pregnancies', patientId],
    queryFn: () => maternityService.getPregnancies(patientId),
    enabled: !!patientId,
  })

  const activePregnancy = pregnancies?.find(p => p.status === 'active')

  // Create a map to track which codes are added as conditions
  // We match by condition name (display) since that's what we store
  const conditionCodeMap = useMemo(() => {
    const map = new Map<string, string>()
    if (clinicalHistory?.conditions) {
      clinicalHistory.conditions.forEach((condition) => {
        // Match by condition name (normalized for comparison)
        const normalizedName = condition.condition.toLowerCase().trim()
        map.set(normalizedName, condition.id)
        // Also store by ICD-10 code if available for additional matching
        if (condition.icd10Code) {
          map.set(`ICD10:${condition.icd10Code}`, condition.id)
        }
      })
    }
    return map
  }, [clinicalHistory])

  // Check if a code is already added as a condition
  const isCodeAddedAsCondition = (code: ConsultationCode): string | null => {
    // Primary match: by display name (what we store in condition.condition)
    const normalizedDisplay = code.display.toLowerCase().trim()
    if (conditionCodeMap.has(normalizedDisplay)) {
      return conditionCodeMap.get(normalizedDisplay) || null
    }
    // Secondary match: by ICD-10 code if we have a mapping
    // Note: This requires checking the terminology result for ICD-10 mappings
    // For now, we'll rely on name matching which is more reliable
    return null
  }

  const createMutation = useMutation({
    mutationFn: (data: ConsultationFormData) => consultationService.create(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
      // Refetch appointments to reflect status change to 'in-progress'
      if (appointmentId) {
        await queryClient.refetchQueries({ queryKey: ['appointments'] })
      }
      onSuccess?.()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<ConsultationFormData>) => consultationService.update(consultation!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      queryClient.invalidateQueries({ queryKey: ['consultation', consultation!.id] })
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
      onSuccess?.()
    },
  })

  const completeMutation = useMutation({
    mutationFn: () =>
      consultationService.complete(consultation!.id, {
        reason: formData.reason,
        notes: formData.notes,
        diagnosis: formData.diagnosis,
        plan: formData.plan,
        codes: formData.codes,
        patientInstructions: formData.patientInstructions,
        instructionsGiven: formData.instructionsGiven,
        instructionsGivenAt: formData.instructionsGivenAt,
        instructionsGivenBy: formData.instructionsGivenBy,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      queryClient.invalidateQueries({ queryKey: ['consultation', consultation!.id] })
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
      // Refetch appointments to reflect status change to 'completed'
      if (consultation?.appointmentId) {
        await queryClient.refetchQueries({ queryKey: ['appointments'] })
      }
      onSuccess?.()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.staffId) {
      alert('Please select a clinical staff member')
      return
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(formData)
      } else {
        await createMutation.mutateAsync(formData)
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to save consultation')
    }
  }

  const handleComplete = async () => {
    if (!formData.notes) {
      alert('Please add notes before completing the consultation')
      return
    }

    if (window.confirm('Complete this consultation? This will set the status to completed.')) {
      try {
        await completeMutation.mutateAsync()
      } catch (error: any) {
        alert(error?.message || 'Failed to complete consultation')
      }
    }
  }

  const handleAddDiagnosis = () => {
    if (newDiagnosis.trim()) {
      setFormData({
        ...formData,
        diagnosis: [...(formData.diagnosis || []), newDiagnosis.trim()],
      })
      setNewDiagnosis('')
    }
  }

  const handleRemoveDiagnosis = (index: number) => {
    const updated = [...(formData.diagnosis || [])]
    updated.splice(index, 1)
    setFormData({ ...formData, diagnosis: updated })
  }

  const handleAddCode = (result: TerminologySearchResult) => {
    const now = new Date().toISOString()
    const newCode: ConsultationCode = {
      system: result.system,
      code: result.code,
      display: result.display,
      version: result.version,
      createdAt: now,
    }
    const existing = formData.codes || []
    // Avoid duplicates by system+code
    if (existing.some((c) => c.system === newCode.system && c.code === newCode.code)) {
      return
    }
    setFormData({
      ...formData,
      codes: [...existing, newCode],
    })
    setCodeSearch('')
    setCodeResults([])
  }

  const handleRemoveCode = (index: number) => {
    const existing = formData.codes || []
    const updated = [...existing]
    updated.splice(index, 1)
    setFormData({
      ...formData,
      codes: updated,
    })
  }

  const handleSearchCodes = async (query: string) => {
    setCodeSearch(query)
    if (!query.trim()) {
      setCodeResults([])
      return
    }
    try {
      setIsSearchingCodes(true)
      const results = await terminologyService.search(selectedSystem, query, { limit: 20 })
      setCodeResults(results)
    } catch (error) {
      console.error('Failed to search terminology codes', error)
    } finally {
      setIsSearchingCodes(false)
    }
  }

  // Add condition mutation
  const addConditionMutation = useMutation({
    mutationFn: ({ code, result }: { code: ConsultationCode; result?: TerminologySearchResult }) => {
      const conditionData = {
        condition: code.display,
        status: 'active' as const,
        icd10Code: result?.icd10Mappings?.[0] || undefined, // Only use ICD-10 if available
        notes: `Added from consultation - ${code.system}:${code.code}`,
      }
      return patientHistoryService.addCondition(patientId, conditionData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
    },
  })

  // Remove condition mutation
  const removeConditionMutation = useMutation({
    mutationFn: (conditionId: string) => {
      return patientHistoryService.removeCondition(patientId, conditionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
    },
  })

  const handleAddAsCondition = async (code: ConsultationCode, _index: number) => {
    // Find the original search result if available to get ICD-10 mappings
    let result: TerminologySearchResult | undefined
    if (codeResults.length > 0) {
      result = codeResults.find(
        (r) => r.system === code.system && r.code === code.code
      )
    }
    
    // If not in current results, try to fetch the code details
    if (!result) {
      try {
        const searchResults = await terminologyService.search(code.system, code.code, { limit: 1 })
        result = searchResults.find((r) => r.system === code.system && r.code === code.code)
      } catch (error) {
        console.warn('Could not fetch code details for condition', error)
      }
    }
    
    try {
      await addConditionMutation.mutateAsync({ code, result })
    } catch (error) {
      console.error('Failed to add condition', error)
      alert('Failed to add condition to clinical history')
    }
  }

  const handleRemoveAsCondition = async (conditionId: string) => {
    try {
      await removeConditionMutation.mutateAsync(conditionId)
    } catch (error) {
      console.error('Failed to remove condition', error)
      alert('Failed to remove condition from clinical history')
    }
  }

  return (
    <form className="consultation-form" onSubmit={handleSubmit}>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="staffId">
            Clinical Staff <span className="required">*</span>
          </label>
          <select
            id="staffId"
            value={formData.staffId}
            onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}
            required
            disabled={isEditing}
          >
            <option value="">Select clinical staff...</option>
            {clinicalStaff?.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.firstName} {staff.lastName} ({staff.role})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="consultationDate">Date</label>
          <input
            id="consultationDate"
            type="date"
            value={formData.consultationDate}
            onChange={(e) => setFormData({ ...formData, consultationDate: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="startTime">Start Time</label>
          <input
            id="startTime"
            type="time"
            value={formData.startTime}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="endTime">End Time</label>
          <input
            id="endTime"
            type="time"
            value={formData.endTime}
            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as ConsultationStatus })}
          >
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no-show">No Show</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="reason">Reason for Visit / Chief Complaint</label>
        <input
          id="reason"
          type="text"
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          placeholder="e.g., Annual checkup, chest pain, follow-up"
        />
      </div>

      <div className="form-group">
        <label htmlFor="notes">Clinical Notes</label>
        <textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={8}
          placeholder="Enter clinical notes, examination findings, assessment..."
        />
      </div>

      <div className="form-group">
        <label>Diagnosis</label>
        <div className="diagnosis-input-group">
          <input
            type="text"
            value={newDiagnosis}
            onChange={(e) => setNewDiagnosis(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddDiagnosis()
              }
            }}
            placeholder="Enter diagnosis code or description"
          />
          <button type="button" className="btn-secondary" onClick={handleAddDiagnosis}>
            Add
          </button>
        </div>
        {formData.diagnosis && formData.diagnosis.length > 0 && (
          <div className="diagnosis-list">
            {formData.diagnosis.map((diag, index) => (
              <span key={index} className="diagnosis-tag">
                {diag}
                <button
                  type="button"
                  className="diagnosis-remove"
                  onClick={() => handleRemoveDiagnosis(index)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Coded Diagnoses</label>
        <div className="coded-diagnosis-header">
          <div className="coded-diagnosis-system-toggle">
            <label htmlFor="terminologySystem">Terminology</label>
            <select
              id="terminologySystem"
              value={selectedSystem}
              onChange={(e) =>
                setSelectedSystem(e.target.value as ConsultationTerminologySystem)
              }
            >
              <option value="ICPC2">ICPC-2</option>
              <option value="ICD10">ICD-10</option>
            </select>
          </div>
        </div>
        <div className="diagnosis-input-group">
          <input
            type="text"
            value={codeSearch}
            onChange={(e) => handleSearchCodes(e.target.value)}
            placeholder={`Search ${selectedSystem} codes...`}
          />
        </div>
        {isSearchingCodes && <div className="coded-diagnosis-loading">Searching codes...</div>}
        {codeResults.length > 0 && (
          <div className="coded-diagnosis-results">
            {codeResults.map((result) => (
              <button
                key={`${result.system}-${result.code}`}
                type="button"
                className="coded-diagnosis-result"
                onClick={() => handleAddCode(result)}
              >
                <span className="coded-diagnosis-code">
                  {result.system}:{result.code}
                </span>
                <span className="coded-diagnosis-display">{result.display}</span>
              </button>
            ))}
          </div>
        )}
        {formData.codes && formData.codes.length > 0 && (
          <div className="coded-diagnosis-list">
            {formData.codes.map((code, index) => {
              const conditionId = isCodeAddedAsCondition(code)
              const isAdded = !!conditionId
              return (
                <span
                  key={`${code.system}-${code.code}-${index}`}
                  className={`diagnosis-tag ${isAdded ? 'diagnosis-tag-condition' : ''}`}
                >
                  {code.system}:{code.code} – {code.display}
                  <button
                    type="button"
                    className="diagnosis-add-condition"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (isAdded && conditionId) {
                        handleRemoveAsCondition(conditionId)
                      } else {
                        handleAddAsCondition(code, index)
                      }
                    }}
                    disabled={addConditionMutation.isPending || removeConditionMutation.isPending}
                    title={isAdded ? 'Remove from Clinical Conditions' : 'Add to Clinical Conditions'}
                    aria-label={isAdded ? 'Remove from Clinical Conditions' : 'Add to Clinical Conditions'}
                  >
                    {isAdded ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.6667 3.5L5.25 9.91667L2.33334 7"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7 2.33334V11.6667M2.33334 7H11.6667"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="diagnosis-remove"
                    onClick={() => handleRemoveCode(index)}
                  >
                    ×
                  </button>
                </span>
              )
            })}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="plan">Treatment Plan</label>
        <textarea
          id="plan"
          value={formData.plan}
          onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
          rows={4}
          placeholder="Enter treatment plan, medications, referrals, follow-up instructions..."
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="followUpDate">Follow-up Date</label>
          <input
            id="followUpDate"
            type="date"
            value={formData.followUpDate}
            onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
          />
        </div>
      </div>

      {formData.followUpDate && (
        <div className="form-group">
          <label htmlFor="followUpNotes">Follow-up Notes</label>
          <textarea
            id="followUpNotes"
            value={formData.followUpNotes}
            onChange={(e) => setFormData({ ...formData, followUpNotes: e.target.value })}
            rows={3}
            placeholder="Notes for follow-up appointment..."
          />
        </div>
      )}

      <div className="form-section">
        <h3>Patient Instructions</h3>
        <div className="form-group">
          <label htmlFor="patientInstructions">Instructions for Patient</label>
          <textarea
            id="patientInstructions"
            value={formData.patientInstructions || ''}
            onChange={(e) => setFormData({ ...formData, patientInstructions: e.target.value })}
            rows={4}
            placeholder="Enter patient-specific instructions..."
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.instructionsGiven || false}
              onChange={(e) => {
                const currentUser = authService.getCurrentUser()
                setFormData({
                  ...formData,
                  instructionsGiven: e.target.checked,
                  instructionsGivenAt: e.target.checked ? new Date().toISOString() : '',
                  instructionsGivenBy: e.target.checked && currentUser ? currentUser.id : '',
                })
              }}
            />
            <span>Instructions Given to Patient</span>
          </label>
        </div>
      </div>

      {activePregnancy && (
        <div className="form-section maternity-visit-section">
          <h3>Maternity Care</h3>
          <p className="section-description">
            Record a maternity visit for this consultation
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setSelectedPregnancy(activePregnancy)
              setShowMaternityVisitForm(true)
            }}
          >
            Record Maternity Visit
          </button>
        </div>
      )}

      <div className="form-actions">
        {isEditing && consultation?.status === 'in-progress' && (
          <button
            type="button"
            className="btn-primary"
            onClick={handleComplete}
            disabled={completeMutation.isPending}
          >
            {completeMutation.isPending ? 'Completing...' : 'Complete Consultation'}
          </button>
        )}
        <button
          type="submit"
          className="btn-primary"
          disabled={createMutation.isPending || updateMutation.isPending}
        >
          {createMutation.isPending || updateMutation.isPending
            ? 'Saving...'
            : isEditing
            ? 'Update Consultation'
            : 'Create Consultation'}
        </button>
      </div>

      {showMaternityVisitForm && selectedPregnancy && (
        <Modal
          isOpen={showMaternityVisitForm}
          title="Record Maternity Visit"
          onClose={() => {
            setShowMaternityVisitForm(false)
            setSelectedPregnancy(null)
          }}
          size="large"
        >
          <MaternityVisitForm
            patientId={patientId}
            pregnancyId={selectedPregnancy.pregnancyId}
            visit={consultation ? {
              visitId: '',
              pregnancyId: selectedPregnancy.pregnancyId,
              patientId,
              visitDate: formData.consultationDate,
              visitType: 'antenatal',
              gestationalAge: '',
              consultationId: consultation.id,
              interventions: [],
              createdAt: '',
              updatedAt: '',
            } : undefined}
            onSuccess={() => {
              setShowMaternityVisitForm(false)
              setSelectedPregnancy(null)
              queryClient.invalidateQueries({ queryKey: ['maternity-visits', selectedPregnancy.pregnancyId] })
            }}
            onCancel={() => {
              setShowMaternityVisitForm(false)
              setSelectedPregnancy(null)
            }}
          />
        </Modal>
      )}
    </form>
  )
}

